import type { Item } from "@tuit/shared-types";
import { formatAge } from "../utils/dateFormat.js";
import { getFaviconUrl } from "../utils/favicon.js";
import styles from "../styles/components/ItemRow.module.css";

export interface ItemRowProps {
  item: Item;
  isRead: boolean;
  onRead: (id: string) => void;
  source?: {
    title: string;
    domain: string | null;
    iconBackground?: string;
    faviconUrl?: string | null;
  };
  highlight?: string;
  mobile?: boolean;
}

export type SharedProps = Omit<ItemRowProps, "mobile">;

export function highlightText(
  text: string,
  query: string | undefined,
): React.ReactNode {
  if (!query) return text;
  const q = query.toLowerCase();
  const parts: React.ReactNode[] = [];
  let last = 0;
  let i = text.toLowerCase().indexOf(q, last);
  while (i !== -1) {
    if (i > last) parts.push(text.slice(last, i));
    parts.push(
      <mark key={i} className={styles.highlight}>
        {text.slice(i, i + query.length)}
      </mark>,
    );
    last = i + query.length;
    i = text.toLowerCase().indexOf(q, last);
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export { formatAge, styles };

// Thumbnail + titleRow (favicon, sourceLabel, title anchor) with a slot for the meta section
export function RowSharedContent({
  item,
  source,
  highlight,
  onRead,
  metaSlot,
}: SharedProps & { metaSlot?: React.ReactNode }) {
  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    window.open(item.url, "_blank");
    onRead(item.id);
  }

  return (
    <>
      {source?.domain && (
        <span
          className={styles.sourceFaviconWrap}
          style={
            source.iconBackground
              ? { background: source.iconBackground }
              : undefined
          }
        >
          <img
            src={
              source.faviconUrl ??
              (source.domain ? getFaviconUrl(source.domain) : "")
            }
            width={24}
            height={24}
            alt=""
            aria-hidden="true"
            className={styles.sourceFavicon}
          />
        </span>
      )}
      {item.thumbnail && (
        <img
          className={styles.thumb}
          src={item.thumbnail}
          alt=""
          width={52}
          height={52}
          loading="lazy"
        />
      )}
      <div className={styles.body}>
        <div className={styles.titleRow}>
          {source && <span className={styles.sourceLabel}>{source.title}</span>}
          <a
            className={styles.title}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            onAuxClick={(e) => {
              if (e.button === 1) onRead(item.id);
            }}
          >
            {highlightText(item.title, highlight)}
          </a>
          {metaSlot}
        </div>
      </div>
    </>
  );
}
